import { generateUUID } from '..'
import TcpServer from './tcp-server.class'

export class SessionManager {

  // PREPARING -> ACTIVE -> STOPPING
  static STATUS = {
    PREPARING: 'PREPARING', // port scan, tcp open 전
    STOPPING: 'STOPPING',   // PREPARING 중 STOP 요청 받았을 경우
    ACTIVE: 'ACTIVE',
  }
  /**
   * @typedef Session
   * @property {SessionManager.STATUS} status
   * @property {string} sessionId
   * @property {TcpServer} tcpServer
   * @property {string} moduleName
   * @property {number} port
   * @property {Function} listener
   */
  /**
   * @type {Object<string, Session>}
   */
  sessionMap = {}

  constructor() {}
  
  getSessionCount() {
    return Object.keys(this.sessionMap).length
  }
  /**
   *  TCP Server 를 오픈하고 관리합니다
   * 
   * @returns {{ moduleName: string, sessionId: string, port: number, error?: Error }}
   */
  async createSession({ moduleName, port: userPort, address = '127.0.0.1', listener } = {}) {
      const sessionId = generateUUID()

    try {
      this._addSession({ moduleName, sessionId, listener })
      const tryPort = this._getUnusePort(userPort)
      const { tcpServer, port } = await this._startTcpServer({ port: tryPort, listener, sessionId })
      this._addSessionInfo(sessionId, { tcpServer, port })
      
      if (this._isUnavailableSession(sessionId)) {
        await this._destorySession(sessionId)
        return {}
      } else {
        this._activateSessionStatus(sessionId)
      }

      return { moduleName, sessionId, port }

    } catch (error) {
      await this._destorySession(sessionId)
      throw error
    }
  }

  /**
   * @param {string} moduleName - 일치하는 session 을 모두 종료합니다
   * @param {string} sessionId - 특정 sessionId 만 종료합니다
   * @returns {{ deleted: boolean, hsids?: string[], error?: Error }}
   */
  async removeSession(moduleName, sessionId) {
    const result = await this._removeSession(moduleName, sessionId)
    return result
  }

  async removeAllSession() {
    await this._removeAllSession()
  }

  _addSession({ moduleName, sessionId, listener }) {
    this._closePreviousSessionIfExist(sessionId)
    this.sessionMap[sessionId] = { moduleName, sessionId, listener, status: SessionManager.STATUS.PREPARING }
  }
  /**
   * 
   * @param {string} sessionId 
   * @param {Session} session 
   */
  _addSessionInfo(sessionId, session = {}) {
    this.sessionMap[sessionId] = { ...this.sessionMap[sessionId], ...session }
  }
  _activateSessionStatus(sessionId) {
    if (this.sessionMap[sessionId]) {
      this.sessionMap[sessionId].status = SessionManager.STATUS.ACTIVE
    }
  }
  _stopSessionStatus(sessionId) {
    if (this.sessionMap[sessionId]) {
      this.sessionMap[sessionId].status = SessionManager.STATUS.STOPPING
    }
  }
  
  _isUnavailableSession(sessionId) {
    return this.sessionMap[sessionId].status === SessionManager.STATUS.STOPPING
  }
  _isActiveSession(sessionId) {
    return this.sessionMap[sessionId].status === SessionManager.STATUS.ACTIVE
  }
  /**
   *  port 부터 1씩 증가하며 사용 가능한 port 로 TCP Server open
   * 
   * @returns {{ port: number, tcpServer: TcpServer }} 
   */
  async _startTcpServer({ address, port, listener, sessionId }) {
    const tcpServer = TcpServer.create({ sessionId, address, port })
    return new Promise((res, rej) => {
      tcpServer.startServerToHook({
        hookCallback: listener,
        listenCallback: (error, port) => {
          if (error) { rej({ error, port }) }
          else { res({ tcpServer, port }) }
        },
      })
    })
  }

  _getUnusePort(port) {
    const ports = Object.values(this.sessionMap).map(s => s.port)
    while(ports.includes(port)) { port++ }
    return port
  }

  async _removeAllSession() {
    for (const [id, session] of Object.entries(this.sessionMap)) {
      try {
        session.tcpServer && await session.tcpServer.closeServer()
        delete this.sessionMap[id]
      } catch (error) {
      }
    }

    this.sessionMap = {}
  }

  async _removeSession(moduleName, sessionId) {
    const retObj = { deleted: false }
    try {
      if (sessionId) {
        const session = this.sessionMap[sessionId]
        if (session && session.moduleName === moduleName) {
          await this._destorySession(sessionId)
          retObj.deleted = true
          retObj['hsids'] = [sessionId]
        }
  
      } else if (moduleName) {
        const ids = []
        for (const [id, session] of Object.entries(this.sessionMap)) {
          if (session.moduleName === moduleName) {
            this._isActiveSession(id) ? await this._destorySession(id) : this._stopSessionStatus(id)
            ids.push(id)
          }
        }
        retObj.deleted = true
        retObj['hsids'] = ids
      }
    } catch (error) {
      retObj.deleted = false
      retObj['error'] = error
    }

    return retObj
  }

  async _destorySession(sessionId) {
    const { tcpServer } = this.sessionMap[sessionId] || {}
    tcpServer && await tcpServer.closeServer()
    delete this.sessionMap[sessionId]
  }

  async _closePreviousSessionIfExist(sessionId) {
    const { tcpServer } = this.sessionMap[sessionId] || {}
    if (tcpServer) {
      delete this.sessionMap[id]  // startHttpHook 을 비동기로 호출하는 경우 대응을 위해 미리 제거
      try {
        await tcpServer.closeServer()
      } catch (error) {
        console.warn(`Can not close existing tcp for httpHook[${sessionId}]`)
      }
    }
  }

  _logSessions(at = '') {
    let logStr = ''
    for (const [key, value] of Object.entries(this.sessionMap)) {
      logStr += `session[${key}]: port[${value.port}] moduleName[${value.moduleName}]\n`
    }
    console.log(`[sessionMan:_logSessions] at[${at}]\n${logStr}`)
  }
}
